import { FC, useState, useCallback } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Button, InlineLoading } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { PlainLink } from 'components/link/link';
import { format0, format1 } from 'utils/number';
import request from 'lib/request';
import { useMissingWellAssignments } from './use-missing-well-assignments';
import type {
  MissingWellAssignment,
  MissingWellAssignmentUnit,
  TriggerResponse,
} from './types';
import styles from './page.module.scss';

const columnHelper = createColumnHelper<MissingWellAssignment>();

const columns = [
  columnHelper.accessor('uwi', {
    id: 'uwi',
    header: 'UWI',
    size: 200,
    meta: { type: 'text', align: 'left' as const },
    cell: (info) => {
      const row = info.row.original;
      return (
        <span title={info.getValue()}>
          <PlainLink href={row.href}>{info.getValue()}</PlainLink>
        </span>
      );
    },
  }),
  columnHelper.accessor('holeDirection', {
    id: 'holeDirection',
    header: 'Hole Direction',
    size: 130,
    meta: { type: 'text', align: 'left' as const },
    cell: (info) => {
      const value = info.getValue() || '--';
      return <span title={value}>{value}</span>;
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Well Status',
    size: 130,
    meta: { type: 'text', align: 'left' as const },
    cell: (info) => {
      const value = info.getValue() || '--';
      return <span title={value}>{value}</span>;
    },
  }),
  columnHelper.accessor('units', {
    id: 'units',
    header: 'Units / Coverage',
    size: 350,
    meta: { align: 'left' as const },
    enableSorting: false,
    cell: (info) => {
      const units: MissingWellAssignmentUnit[] = info.getValue() || [];
      if (units.length === 0) return '--';
      const tooltip = units
        .map((u) => `${u.name} (${format1(u.coverageRatio * 100)}%)`)
        .join(', ');
      return (
        <div className={styles.unitList} title={tooltip}>
          {units.map((unit) => (
            <div key={unit.id} className={styles.unitItem}>
              <PlainLink href={unit.href}>{unit.name}</PlainLink>
              <span className={styles.coverageRatio}>
                ({format1(unit.coverageRatio * 100)}%)
              </span>
            </div>
          ))}
        </div>
      );
    },
  }),
];

export const MissingWellAssignmentsPage: FC = () => {
  const {
    rows,
    isLoading,
    error,
    hasMore,
    totalCount,
    fullyCoveredCount,
    fetchNextPage,
    setSearch,
    setSort,
  } = useMissingWellAssignments();

  const [triggerState, setTriggerState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [triggerMessage, setTriggerMessage] = useState('');

  const handleTrigger = useCallback(async () => {
    setTriggerState('loading');
    setTriggerMessage('');
    const abortController = new AbortController();
    const response = await request.post<TriggerResponse, { error: string }>(
      '/admin/missing_well_assignments/trigger',
      {},
      abortController
    );
    if (response.type === 'success' && response.data?.success) {
      setTriggerState('success');
      setTriggerMessage(
        `Enqueued ${response.data.jobs} Best Fit jobs for ${response.data.count} wells in Sidekiq.`
      );
    } else {
      setTriggerState('error');
      const errorData = response.type === 'error' ? response.data : null;
      setTriggerMessage(
        (errorData as { error?: string } | null)?.error ??
          'Failed to trigger best fit'
      );
    }
  }, []);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Missing Well Assignments"
        actions={
          <div className={styles.headerActions}>
            {triggerState === 'loading' && (
              <InlineLoading description="Enqueuing jobs..." />
            )}
            {triggerState === 'success' && (
              <span className={styles.triggerSuccess}>{triggerMessage}</span>
            )}
            {triggerState === 'error' && (
              <span className={styles.triggerError}>{triggerMessage}</span>
            )}
            <Button
              kind="primary"
              size="sm"
              onClick={handleTrigger}
              disabled={triggerState === 'loading' || fullyCoveredCount === 0}
            >
              Kick off Run Best Fit jobs in Sidekiq (
              {format0(fullyCoveredCount)})
            </Button>
          </div>
        }
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Wells with spatial intersections (UnitIntersectionAnalysis records)
          but no active unit well assignment. These wells have been analyzed but
          not yet assigned to a development area. Excludes vertical wells.
          {totalCount !== null && (
            <span className={styles.rowCount}>
              {' '}
              — {format0(totalCount)} wells found
            </span>
          )}
        </div>
      </div>

      <DataTable
        data={rows}
        columns={columns as ColumnDef<MissingWellAssignment>[]}
        getRowId={(row) => row.uwi as string}
        isLoading={isLoading}
        error={error}
        persistenceKey="missing-well-assignments"
        scroll={{
          mode: 'infinite',
          hasNextPage: hasMore,
          totalCount,
        }}
        onLoadMore={() => fetchNextPage()}
        onGlobalSearchChange={(s) => setSearch(s.globalFilter)}
        onSortChange={(s) => setSort(s.sorting)}
        config={{
          sorting: true,
          globalSearch: true,
          columnFilters: false,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'missing-well-assignments',
              },
            ],
          },
        }}
      />
    </>
  );
};
